import './conversation-date-range-modal.scss';

import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { formatDate } from '../../../../data/utils/date.utils';
import { ContextMenuState } from '../../../../data/utils/use-context-menu';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { ContextModal } from '../../../../elements/context-menu/context-modal';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { FormColumns, FormRow } from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { useConversationDateRangeForm } from './form/use-conversation-date-range-form';

export type DateRange = {
  startDate?: string;
  endDate?: string;
};

type ConversationDateRangeContextModalProps = {
  state: ContextMenuState | false;
  selected?: DateRange | null;
  onSaved?: (dateRange: DateRange | null) => void;
  onClose?: () => void;
};

export function ConversationDateRangeContextModal({
  state,
  selected,
  onSaved,
  onClose,
}: ConversationDateRangeContextModalProps) {
  const { form, formIsValid } = useConversationDateRangeForm();

  useEffect(() => {
    form.setValue('startDate', selected?.startDate ?? '');
    form.setValue('endDate', selected?.endDate ?? '');
  }, [form, selected]);

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');

  const onHandleClear = useCallback(() => {
    form.reset({ startDate: '', endDate: '' });
    onSaved?.(null);
    onClose?.();
  }, [form, onClose, onSaved]);

  const intl = useIntl();

  const dateRange = useMemo(() => {
    let startValue = startDate ?? undefined;
    let endValue = endDate ?? undefined;

    if (startDate && !endDate) {
      endValue = startDate;
    }
    if (endDate && !startDate) {
      startValue = endDate;
    }

    const range =
      !startValue && !endValue
        ? null
        : { startDate: startValue, endDate: endValue };

    if (!range) {
      return null;
    }
    return range;
  }, [endDate, startDate]);

  const onHandleSubmit = useCallback(() => {
    onSaved?.(dateRange);
    onClose?.();
  }, [dateRange, onClose, onSaved]);

  const canClear = startDate !== '' || endDate !== '';

  return (
    <ContextModal className="range-modal" state={state}>
      <FormProvider {...form}>
        <FormRow>
          <InputFieldController
            icon={BaseInputIcon.DATE}
            type="date"
            name="startDate"
            label={<CustomFormattedMessage id="common.start-date" />}
            max={formatDate(new Date())}
          />
        </FormRow>

        <FormRow>
          <InputFieldController
            icon={BaseInputIcon.DATE}
            type="date"
            name="endDate"
            label={<CustomFormattedMessage id="common.end-date" />}
            max={formatDate(new Date())}
          />
        </FormRow>
        {dateRange && (
          <h4 className="display-range">
            <CustomFormattedMessage
              id="common.date-range"
              values={{
                start: intl.formatDate(dateRange.startDate),
                end: intl.formatDate(dateRange.endDate),
              }}
            />
          </h4>
        )}
        <FormColumns>
          <FormRow width="50%">
            <Button
              disabled={!canClear}
              type={ButtonType.SECONDARY}
              icon={ButtonIcon.NONE}
              onClick={onHandleClear}
            >
              <CustomFormattedMessage id="common.clear" />
            </Button>
          </FormRow>
          <FormRow width="50%">
            <Button
              disabled={!formIsValid}
              icon={ButtonIcon.NONE}
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="common.apply" />
            </Button>
          </FormRow>
        </FormColumns>
      </FormProvider>
    </ContextModal>
  );
}
